import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Mail, ChevronLeft, ChevronRight, Check, AlertCircle, Loader2,
  Heart, Sparkles, ShieldCheck, ArrowRight, LogIn, Leaf, Activity, Package
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_nutrifit-portal/artifacts/s4srivcq_Logo%20Icono%20Solo%20Nutriquimia%20SVG.svg";

const ClientRegistrationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo;
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [distributorName, setDistributorName] = useState('');
  const [phase, setPhase] = useState('intro'); // intro | register | success
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [clientName, setClientName] = useState('');
  const [errorType, setErrorType] = useState(null); // 'network' | 'invalid' | null
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: '', cedula: '', gender: '',
    company: '', medical_insurance: '', height: '', weight: '', address: ''
  });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const validate = async (attempt = 1) => {
      try {
        setValidating(true);
        setErrorType(null);
        const res = await axios.get(`${API}/auth/registration-info/${token}`, {
          signal: controller.signal,
          timeout: 15000,
        });
        if (!cancelled) {
          setDistributorName(res.data.distributor_name);
          setValid(true);
          setValidating(false);
        }
      } catch (err) {
        if (cancelled || axios.isCancel(err)) return;
        // Auto-retry once on network errors
        if (attempt < 2 && (!err.response || err.code === 'ECONNABORTED')) {
          await new Promise(r => setTimeout(r, 2000));
          if (!cancelled) return validate(attempt + 1);
        }
        if (!cancelled) {
          setValid(false);
          setErrorType(err.response?.status === 404 ? 'invalid' : 'network');
          setValidating(false);
        }
      }
    };

    validate();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  const retryValidation = () => {
    setValidating(true);
    setValid(false);
    setErrorType(null);
    axios.get(`${API}/auth/registration-info/${token}`, { timeout: 15000 })
      .then(res => {
        setDistributorName(res.data.distributor_name);
        setValid(true);
      })
      .catch(err => {
        setValid(false);
        setErrorType(err.response?.status === 404 ? 'invalid' : 'network');
      })
      .finally(() => setValidating(false));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register-client/${token}`, formData, { timeout: 20000 });
      setClientName(formData.name.split(' ')[0]);
      setPhase('success');
    } catch (error) {
      const msg = error.response?.data?.detail || (error.code === 'ECONNABORTED' ? 'Tiempo de espera agotado. Intenta de nuevo.' : 'Error al registrarse. Verifica tu conexión.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitles = ['Acceso', 'Personal', 'Adicional'];
  const canProceed = () => {
    if (step === 0) return formData.email && formData.password && formData.password.length >= 6;
    if (step === 1) return formData.name && formData.gender;
    return true;
  };

  // --- LOADING ---
  if (validating) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
      </div>
    );
  }

  // --- INVALID TOKEN OR NETWORK ERROR ---
  if (!valid) {
    const isNetworkError = errorType === 'network';
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="registration-error-page">
        <div className="max-w-sm w-full text-center">
          <div className={`w-20 h-20 ${isNetworkError ? 'bg-amber-50' : 'bg-red-50'} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
            <AlertCircle size={40} className={isNetworkError ? 'text-amber-400' : 'text-red-400'} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {isNetworkError ? 'Error de Conexión' : 'Link Inválido'}
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            {isNetworkError
              ? 'No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta de nuevo.'
              : 'Este link de registro no es válido o ha expirado. Contacta a tu distribuidor para obtener uno nuevo.'}
          </p>
          <div className="flex flex-col gap-3">
            {isNetworkError && (
              <Button onClick={retryValidation}
                className="text-white rounded-full px-8 font-bold shadow-lg"
                style={{ background: 'linear-gradient(135deg, #3A601C, #467322)', boxShadow: '0 10px 25px -5px rgba(58, 96, 28, 0.3)' }}
                data-testid="retry-validation-btn">
                Reintentar
              </Button>
            )}
            <Button onClick={() => navigate('/')} variant={isNetworkError ? 'outline' : 'default'}
              className={isNetworkError ? 'border-gray-200 text-gray-600 rounded-full px-8' : 'bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-full px-8 font-bold shadow-lg shadow-emerald-500/25'}
              data-testid="go-home-btn">
              Ir al Inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- INTRO / WELCOME ---
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="registration-intro-page">
        <div className="fixed top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="fixed bottom-0 left-0 w-64 h-64 bg-green-500/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />

        <div className="flex-1 flex items-center justify-center px-4 py-8 relative">
          <div className="max-w-md w-full">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <img src={LOGO_URL} alt="Nutriquimia" className="w-14 h-14 object-contain" />
              <span className="font-bold text-gray-800 text-2xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Nutriquimia App
              </span>
            </div>

            {/* Welcome Card */}
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100/50 overflow-hidden">
              <div className="h-2" style={{ background: 'linear-gradient(90deg, #3A601C, #467322, #5A8A2D, #467322)' }} />
              
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl flex items-center justify-center mx-auto mb-5 border border-emerald-100/50">
                  <Sparkles size={36} style={{ color: '#467322' }} />
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Bienvenido a Nutriquimia
                </h1>
                <p className="text-sm text-gray-500 mb-1">
                  Has sido invitado por
                </p>
                <p className="text-lg font-bold mb-6" style={{ color: '#467322' }}>
                  {distributorName}
                </p>

                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  Al presionar <span className="font-semibold" style={{ color: '#467322' }}>Iniciar</span>, comenzarás tu registro como usuario de la plataforma Nutriquimia App. Solo te tomará un par de minutos.
                </p>

                {/* Features */}
                <div className="space-y-3 mb-8 text-left">
                  {[
                    { icon: Activity, title: 'Test de Salud', desc: 'Evalúa tu bienestar físico con nuestro test especializado' },
                    { icon: Heart, title: 'Recetas Personalizadas', desc: 'Recibe recetas médicas adaptadas a tus necesidades' },
                    { icon: Package, title: 'Suplementos', desc: 'Accede al catálogo de suplementos nutricionales' },
                    { icon: ShieldCheck, title: 'Seguimiento Continuo', desc: 'Registra tus analíticas y lleva el control de tu salud' },
                  ].map((feature, i) => (
                    <div key={feature.title} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50/80 border border-gray-100/50">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #e8f5e0, #d4edcc)' }}>
                        <feature.icon size={18} style={{ color: '#467322' }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">{feature.title}</p>
                        <p className="text-xs text-gray-500 leading-relaxed">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setPhase('register')}
                  className="w-full flex items-center justify-center gap-2 text-white py-3.5 rounded-full text-base font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg, #3A601C, #467322)', boxShadow: '0 10px 25px -5px rgba(58, 96, 28, 0.3)' }}
                  data-testid="start-registration-btn"
                >
                  Iniciar Registro
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mt-5">
              Tu distribuidor te acompañará en este proceso de bienestar.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- SUCCESS ---
  if (phase === 'success') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="registration-success-page">
        <div className="fixed top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="fixed bottom-0 left-0 w-64 h-64 bg-green-500/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />

        <div className="flex-1 flex items-center justify-center px-4 py-8 relative">
          <div className="max-w-md w-full">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <img src={LOGO_URL} alt="Nutriquimia" className="w-14 h-14 object-contain" />
              <span className="font-bold text-gray-800 text-2xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Nutriquimia App
              </span>
            </div>

            {/* Success Card */}
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100/50 overflow-hidden">
              <div className="h-2" style={{ background: 'linear-gradient(90deg, #3A601C, #467322, #5A8A2D, #467322)' }} />
              
              <div className="p-8 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-emerald-50 to-green-50 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-emerald-100/50">
                  <Check size={44} style={{ color: '#467322' }} strokeWidth={3} />
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Registro Exitoso
                </h1>
                <p className="text-lg text-gray-600 mb-2">
                  {clientName ? `${clientName}, tu` : 'Tu'} cuenta ha sido creada correctamente.
                </p>
                <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                  Ya eres parte de la familia <span className="font-semibold" style={{ color: '#467322' }}>Nutriquimia</span>. Ahora puedes iniciar sesión y comenzar a explorar todas las herramientas disponibles para cuidar tu salud y bienestar.
                </p>

                {/* What's next */}
                <div className="bg-gradient-to-br from-emerald-50/80 to-green-50/80 rounded-2xl p-5 mb-8 border border-emerald-100/30">
                  <p className="text-sm font-bold text-gray-800 mb-3 flex items-center justify-center gap-2">
                    <Leaf size={16} style={{ color: '#467322' }} />
                    ¿Qué puedes hacer ahora?
                  </p>
                  <div className="space-y-2 text-left">
                    {[
                      'Completa tu Test de Salud Física',
                      'Explora los suplementos disponibles',
                      'Consulta con tu distribuidor especialista',
                    ].map((item, i) => (
                      <div key={item} className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#467322' }}>
                          <Check size={12} className="text-white" />
                        </div>
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => returnTo ? navigate(returnTo, { state: { fromRegister: true } }) : navigate('/')}
                  className="w-full flex items-center justify-center gap-2 text-white py-3.5 rounded-full text-base font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg, #3A601C, #467322)', boxShadow: '0 10px 25px -5px rgba(58, 96, 28, 0.3)' }}
                  data-testid="go-to-login-btn"
                >
                  <LogIn size={18} />
                  {returnTo ? 'Iniciar Sesión y Continuar' : 'Acceder a Nutriquimia App'}
                </button>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mt-5">
              Usa el correo y contraseña que registraste para iniciar sesión.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- REGISTRATION FORM ---
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="client-registration-page">
      <div className="fixed top-0 right-0 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-56 h-56 bg-blue-500/5 rounded-full blur-3xl -translate-x-1/2 pointer-events-none" />

      <div className="flex-1 flex items-center justify-center px-4 py-8 relative">
        <div className="max-w-md w-full">
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Nutriquimia" className="w-12 h-12 object-contain" />
              <span className="font-bold text-gray-800 text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Nutriquimia App
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Registro de Cliente
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Invitado por <span className="font-semibold" style={{ color: '#467322' }}>{distributorName}</span>
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100/50 overflow-hidden">
            <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #3A601C, #467322, #5A8A2D)' }} />
            <div className="p-6">
              {/* Step Indicator */}
              <div className="flex items-center justify-center mb-6">
                {stepTitles.map((title, index) => (
                  <div key={title} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                        index < step ? 'bg-emerald-500 text-white' : index === step ? 'bg-emerald-500 text-white ring-4 ring-emerald-500/20' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {index < step ? <Check size={16} /> : index + 1}
                      </div>
                      <span className={`text-[10px] mt-1 font-medium ${index === step ? 'text-emerald-600' : 'text-gray-400'}`}>{title}</span>
                    </div>
                    {index < 2 && <div className={`w-10 h-0.5 mx-1.5 rounded ${index < step ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
                  </div>
                ))}
              </div>

              <form onSubmit={(e) => { e.preventDefault(); step < 2 ? setStep(step + 1) : handleSubmit(e); }} autoComplete="off">
                {step === 0 && (
                  <div className="space-y-4">
                    <div className="text-center mb-2">
                      <h3 className="text-base font-semibold" style={{ color: '#467322' }}>Datos de Acceso</h3>
                      <p className="text-xs text-gray-500">Tu correo será tu usuario para iniciar sesión</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-semibold text-sm">Correo Electrónico *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-400" size={16} />
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required placeholder="correo@ejemplo.com" autoComplete="off"
                          className="pl-10 bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 rounded-xl h-11"
                          data-testid="reg-email" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-semibold text-sm">Contraseña *</Label>
                      <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required placeholder="Mínimo 6 caracteres" autoComplete="new-password"
                        className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 rounded-xl h-11"
                        data-testid="reg-password" />
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-4">
                    <div className="text-center mb-2">
                      <h3 className="text-base font-semibold" style={{ color: '#467322' }}>Información Personal</h3>
                      <p className="text-xs text-gray-500">Datos básicos de tu perfil</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-semibold text-sm">Nombre Completo *</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required placeholder="Nombre y apellido"
                        className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11"
                        data-testid="reg-name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-semibold text-sm">Género *</Label>
                      <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                        <SelectTrigger className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11" data-testid="reg-gender">
                          <SelectValue placeholder="Seleccionar género" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-gray-100 rounded-xl">
                          <SelectItem value="Masculino">Masculino</SelectItem>
                          <SelectItem value="Femenino">Femenino</SelectItem>
                          <SelectItem value="Prefiero no decir">Prefiero no decir</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-semibold text-sm">Teléfono</Label>
                        <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+1234567890" className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11"
                          data-testid="reg-phone" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-semibold text-sm">Cédula</Label>
                        <Input value={formData.cedula} onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                          placeholder="Número de ID" className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11"
                          data-testid="reg-cedula" />
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <div className="text-center mb-2">
                      <h3 className="text-base font-semibold" style={{ color: '#467322' }}>Información Adicional</h3>
                      <p className="text-xs text-gray-500">Datos complementarios (opcionales)</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-semibold text-sm">Empresa</Label>
                        <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          placeholder="Nombre" className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-semibold text-sm">Seguro Médico</Label>
                        <Input value={formData.medical_insurance} onChange={(e) => setFormData({ ...formData, medical_insurance: e.target.value })}
                          placeholder="Nombre" className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-semibold text-sm">Estatura (pies)</Label>
                        <Input value={formData.height} onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                          placeholder="Ej: 5'8" className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-semibold text-sm">Peso</Label>
                        <Input value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                          placeholder="Ej: 150 lbs" className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-semibold text-sm">Dirección</Label>
                      <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Dirección completa" className="bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 rounded-xl h-11" />
                    </div>
                  </div>
                )}

                <div className="flex justify-between mt-6 pt-4 border-t border-gray-100">
                  {step > 0 ? (
                    <Button type="button" variant="outline" onClick={() => setStep(step - 1)}
                      className="border-gray-200 text-gray-600 rounded-full">
                      <ChevronLeft size={16} className="mr-1" /> Anterior
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setPhase('intro')}
                      className="border-gray-200 text-gray-600 rounded-full">
                      <ChevronLeft size={16} className="mr-1" /> Volver
                    </Button>
                  )}
                  {step < 2 ? (
                    <Button type="submit" disabled={!canProceed()}
                      className="text-white rounded-full font-bold shadow-lg shadow-emerald-500/25"
                      style={{ background: 'linear-gradient(135deg, #3A601C, #467322)' }}>
                      Siguiente <ChevronRight size={16} className="ml-1" />
                    </Button>
                  ) : (
                    <Button type="submit" disabled={submitting}
                      className="text-white rounded-full font-bold shadow-lg shadow-emerald-500/25"
                      style={{ background: 'linear-gradient(135deg, #3A601C, #467322)' }}
                      data-testid="submit-registration">
                      {submitting ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Check size={16} className="mr-1" />}
                      Registrarme
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            Al registrarte, aceptas los términos y condiciones de Nutriquimia.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientRegistrationPage;
